import type { CardIdentity, CardRecord, ChecklistSourceSelection } from './types';
import { cardIdentitySerialIsValid } from './types';
import type { CardPresentation } from './card-presentation';
import { parseBatchTransferPresentation } from './batch-transfer-presentation';
import {
  parseBatchTransferItemSpecifics,
  type BatchTransferItemSpecifics,
} from './batch-transfer-specifics';
import { EBAY_SPORT_CARD_FEATURES } from './ebay-seller-hub';
import { CHECKLIST_ISSUE_TYPES, type ChecklistIssueType } from './checklist';

export type BatchImportMode = 'DRAFT' | 'MANUAL_CONFIRMED';
export interface BatchImportCatalogWarning {
  code: string;
  message: string;
  field?: string;
  current?: unknown;
  /** Checklist field absent; null current remains an explicit correction removal. */
  currentState?: 'MISSING';
  proposed?: unknown;
  resolution: string;
}
/**
 * Row-level catalog link of an imported card (RFC-0004 R1). The package's product-wide snapshot fingerprint is
 * evidence only; the link is to the exact current row + parallel definition that every identity fact equals.
 */
export interface BatchImportCatalogLink {
  /** Digest of the linked roster row and parallel definition, excluding product-wide snapshot fingerprints. */
  linkedRowVersion: string;
  packageSnapshotFingerprint?: string;
  packageParallelSnapshotFingerprint?: string;
  /** Observed facts the linked row does not state; kept as evidence, never as identity. */
  observedFacts?: Partial<CardIdentity>;
}
/** Language a confirmed import observed but the linked catalog row does not state (evidence, never identity). */
export function importedObservedLanguage(card: {
  importedIdentification?: unknown;
}): string | undefined {
  const provenance = card.importedIdentification as
    | { catalogLink?: { observedFacts?: { language?: unknown } } }
    | undefined;
  const language = provenance?.catalogLink?.observedFacts?.language;
  return typeof language === 'string' && language.trim() ? language.trim() : undefined;
}
/** Catalog issue types that assert no catalog row exists, the only basis for unlinked manual verification. */
export const BATCH_IMPORT_NO_ROW_ISSUE_TYPES = [
  'MISSING_CARD',
  'MISSING_SUBSET',
  'MISSING_PARALLEL',
] as const satisfies readonly ChecklistIssueType[];
export interface BatchImportFieldDifference {
  field: string;
  current?: unknown;
  proposed?: unknown;
}
export const BATCH_IMPORT_CLIENT_FAILURE_CODES = [
  'INVALID_JSON',
  'INVALID_FORMAT',
  'FILE_TOO_LARGE',
  'WRONG_BATCH',
  'FILE_READ_FAILED',
] as const;
export interface BatchImportDiagnosticReport {
  reportId: string;
  batchId: string;
  createdAt: string;
  actor: string;
  requestId: string;
  stage: 'VALIDATION' | 'PREVIEW' | 'APPLY' | 'CLIENT' | 'CORRECTION';
  importMode: BatchImportMode;
  exportId?: string;
  runId?: string;
  reason?: string;
  resolution?: string;
  fileName?: string;
  fileDigest?: string;
  rows?: (BatchImportPreviewRow | BatchImportOutcome)[];
  result?: Record<string, unknown>;
}
export const BATCH_TRANSFER_MAX_CARDS = 200;
export const BATCH_TRANSFER_MAX_APPLY_CARDS = 10;
export const BATCH_TRANSFER_MAX_MANIFEST_BYTES = 10 * 1024 ** 2;
export const BATCH_TRANSFER_MAX_ARCHIVE_BYTES = 1024 ** 3;
export const BATCH_TRANSFER_MAX_IMAGE_BYTES = 20 * 1024 ** 2;
export const BATCH_TRANSFER_MAX_IMPORT_BYTES = 2 * 1024 ** 2;
export const BATCH_TRANSFER_RETENTION_DAYS = 30;
export const BATCH_TRANSFER_ARCHIVE_DAYS = 1;
export const BATCH_TRANSFER_SCHEMA_VERSION = 2;
export const BATCH_TRANSFER_RESULT_STATUSES = [
  'MATCHED',
  'AMBIGUOUS',
  'NO_MATCH',
  'NOT_PROCESSED',
] as const;
export interface BatchTransferProducer {
  tool: string;
  model?: string;
  version?: string;
}
export interface BatchTransferSource {
  sourceSystem: string;
  sourceTargetId: string;
  sourceItemId: string;
  snapshotFingerprint?: string;
  parallelDefinitionId?: string;
}
/** Research suggestions remain separate from operator-reviewed listing fields. */
export interface BatchTransferListing {
  title?: string;
  /** Optional v2 research suggestion only; import never confers condition authority. */
  presentation?: CardPresentation;
  /** Plain text; reviewers explicitly adopt it into their description editor. */
  description?: string;
  suggestedPrice?: { amountMinor: number; currency: 'USD' };
  priceBasis?: string;
  priceSources?: {
    url: string;
    kind: 'SOLD' | 'ACTIVE' | 'OTHER';
    amountMinor?: number;
    currency?: 'USD';
  }[];
  features?: (typeof EBAY_SPORT_CARD_FEATURES)[number][];
  /** Optional v2 Finish/Rarity research; omission preserves saved specifics. */
  itemSpecifics?: BatchTransferItemSpecifics;
}
export const BATCH_TRANSFER_CAPTURE_ISSUE_TYPES = ['SIDES_SWAPPED', 'UNREADABLE', 'OTHER'] as const;
/**
 * Fine deskew below this many degrees is noise: no new image is made (owner decision 2026-10-20). Uploads that
 * carry a smaller angle are rejected so measurement jitter can never create replacement images.
 */
export const BATCH_TRANSFER_MIN_DESKEW_DEGREES = 2;
/** Larger tilts are expressed as a quarter turn plus a smaller deskew. */
export const BATCH_TRANSFER_MAX_DESKEW_DEGREES = 45;
/**
 * Reviewed orientation fix for one ORIGINAL photo side, applied by TCVS when the card is manually confirmed.
 * `quarterTurns` = clockwise 90-degree turns; `deskewDegrees` = further clockwise degrees (negative =
 * counter-clockwise), measured on the original photo, multiples of 0.1. TCVS bakes both into the pixels of a new
 * image file and keeps the old file referenced in `imageHistory`.
 */
export interface BatchTransferImageOrientationSide {
  quarterTurns?: 0 | 1 | 2 | 3;
  deskewDegrees?: number;
}
export type BatchTransferImageOrientation = Partial<
  Record<'front' | 'back', BatchTransferImageOrientationSide>
>;
export interface BatchTransferResearch {
  listing?: BatchTransferListing;
  draftDisagreements?: { field: (typeof BATCH_TRANSFER_IDENTITY_FIELDS)[number]; note: string }[];
  captureIssues?: { type: (typeof BATCH_TRANSFER_CAPTURE_ISSUE_TYPES)[number]; note: string }[];
  catalogIssues?: { type: ChecklistIssueType; note: string }[];
  imageOrientation?: BatchTransferImageOrientation;
}
export interface BatchIdentificationResult extends BatchTransferResearch {
  cardId: string;
  inputFingerprint: string;
  status: (typeof BATCH_TRANSFER_RESULT_STATUSES)[number];
  identity?: CardIdentity;
  confidence?: number;
  evidence?: string[];
  candidates?: { identity: CardIdentity; confidence?: number; evidence?: string[] }[];
  checklistSource?: BatchTransferSource;
  checklistSourceSelection?: ChecklistSourceSelection;
}
export interface BatchIdentificationResults {
  schemaVersion: 1 | 2;
  exportId: string;
  batchId: string;
  runId: string;
  producer: BatchTransferProducer;
  cards: BatchIdentificationResult[];
}
export interface BatchIdentificationImportProvenance extends BatchTransferResearch {
  importId: string;
  runId: string;
  producer: BatchTransferProducer;
  confidence?: number;
  evidence?: string[];
  importedAt: string;
  importMode?: BatchImportMode;
  confirmedBy?: string;
  catalogWarnings?: BatchImportCatalogWarning[];
  suppliedSource?: BatchTransferSource | ChecklistSourceSelection;
  catalogLink?: BatchImportCatalogLink;
}
export interface BatchTransferJob {
  jobId: string;
  batchId: string;
  kind: 'EXPORT' | 'IMPORT' | 'REPORT' | 'PACKAGE';
  packageId?: string;
  createdAt: string;
  status: 'QUEUED' | 'RUNNING' | 'READY' | 'FAILED' | 'APPLIED' | 'PARTIAL';
  progress?: number;
  cardCount?: number;
  imageCount?: number;
  missingImageCount?: number;
  archiveBytes?: number;
  downloadUrl?: string;
  error?: string;
  runId?: string;
  producer?: BatchTransferProducer;
  appliedCount?: number;
  outcomes?: BatchImportOutcome[];
  importMode?: BatchImportMode;
  stage?: BatchImportDiagnosticReport['stage'];
  reportId?: string;
  reason?: string;
}
export interface BatchTransferHistory {
  jobs: BatchTransferJob[];
  cursor?: string;
}
export type BatchImportPreviewStatus =
  | 'READY'
  | 'UNCHANGED'
  | 'ALREADY_APPLIED'
  | 'UNCERTAIN'
  | 'CONFLICT'
  | 'LOCKED'
  | 'INVALID';
export interface BatchImportPreviewRow extends BatchTransferResearch {
  cardId: string;
  shortCode?: string;
  status: BatchImportPreviewStatus;
  current?: CardIdentity;
  proposed?: CardIdentity;
  reason?: string;
  confidence?: number;
  evidence?: string[];
  checklistSourceSelection?: ChecklistSourceSelection | null;
  catalogLink?: BatchImportCatalogLink;
  catalogWarnings?: BatchImportCatalogWarning[];
  differences?: BatchImportFieldDifference[];
  resolution?: string;
  clearsHierarchy?: boolean;
  clearsUnverifiedSource?: boolean;
}
export interface BatchImportPreview {
  importMode?: BatchImportMode;
  reportId?: string;
  previewToken: string;
  exportId: string;
  batchId: string;
  runId: string;
  rows: BatchImportPreviewRow[];
}
export interface BatchImportOrientationOutcome {
  side: 'front' | 'back';
  status: 'APPLIED' | 'ALREADY_APPLIED' | 'SKIPPED';
  reason?: string;
}
export interface BatchImportOutcome {
  cardId: string;
  catalogWarnings?: BatchImportCatalogWarning[];
  imageOrientation?: BatchImportOrientationOutcome[];
  resolution?: string;
  status: 'APPLIED' | 'ALREADY_APPLIED' | 'SKIPPED' | 'CONFLICT' | 'FAILED';
  reason?: string;
}
export interface BatchImportApplyResponse {
  importId: string;
  reportId?: string;
  rows: BatchImportOutcome[];
}
export interface BatchTransferImage {
  key: string;
  versionId?: string;
  etag: string;
  sha256?: string;
  bytes: number;
  width?: number;
  height?: number;
  format?: string;
  path?: string;
}
export interface BatchTransferInternalCard {
  cardId: string;
  inputFingerprint: string;
  reviewFingerprint: string;
  baseline: Record<string, unknown>;
  baselineReadiness?: Record<string, unknown> | null;
  images: { front?: BatchTransferImage; back?: BatchTransferImage };
  labels: Record<string, unknown>;
}
export interface BatchTransferInternalManifest {
  schemaVersion: 1;
  exportId: string;
  batchId: string;
  createdAt: string;
  cards: BatchTransferInternalCard[];
}
export const BATCH_TRANSFER_IDENTITY_FIELDS = [
  'category',
  'set',
  'number',
  'name',
  'year',
  'brand',
  'subset',
  'attributes',
  'rookie',
  'autograph',
  'memorabilia',
  'serialNumbered',
  'team',
  'language',
  'variation',
  'serialTo',
  'serialNumber',
] as const;
const boolFields = new Set(['rookie', 'autograph', 'memorabilia', 'serialNumbered']);
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const exactKeys = (v: Record<string, unknown>, allowed: readonly string[]) =>
  Object.keys(v).every((k) => allowed.includes(k));
const text = (v: unknown, maximum = 300): v is string =>
  typeof v === 'string' && v.trim().length > 0 && v.length <= maximum;
const confidence = (v: unknown) =>
  v === undefined || (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1);
const evidence = (v: unknown) =>
  v === undefined || (Array.isArray(v) && v.length <= 10 && v.every((x) => text(x, 1000)));
const cardId = (v: unknown) =>
  typeof v === 'string' && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(v);
export function batchTransferIdentityIsValid(v: unknown): v is CardIdentity {
  if (!object(v) || !exactKeys(v, BATCH_TRANSFER_IDENTITY_FIELDS)) return false;
  if (!['category', 'set', 'number', 'name'].every((k) => text(v[k]))) return false;
  for (const [k, x] of Object.entries(v)) {
    if (boolFields.has(k)) {
      if (typeof x !== 'boolean') return false;
    } else if (k === 'serialTo' || k === 'serialNumber') {
      if (typeof x !== 'number' || !Number.isInteger(x) || x < 1 || x > 1000000) return false;
    } else if (!text(x, k === 'attributes' ? 1000 : 300)) return false;
  }
  return cardIdentitySerialIsValid(v as unknown as CardIdentity);
}
function selectionIsValid(v: unknown): boolean {
  if (
    !object(v) ||
    !exactKeys(v, [
      'sourceSystem',
      'sourceTargetId',
      'sourceItemId',
      'sourceSubsetId',
      'sourceDefinitionType',
      'snapshotFingerprint',
      'sourceFamilyId',
      'checklistId',
      'naturalKey',
      'subset',
      'parallel',
    ])
  )
    return false;
  if (
    !['sourceSystem', 'sourceTargetId', 'sourceItemId', 'snapshotFingerprint', 'checklistId'].every(
      (k) => text(v[k]),
    )
  )
    return false;
  if (typeof v.subset !== 'string' || v.subset.length > 300 || !object(v.naturalKey)) return false;
  const n = v.naturalKey;
  if (
    !exactKeys(n, ['year', 'brand', 'product', 'number', 'cardSet', 'sourceItemId']) ||
    !text(n.year) ||
    !text(n.number) ||
    ['brand', 'product'].some((k) => typeof n[k] !== 'string' || (n[k] as string).length > 300)
  )
    return false;
  for (const k of ['sourceSubsetId', 'sourceFamilyId'])
    if (v[k] !== undefined && !text(v[k])) return false;
  for (const k of ['cardSet', 'sourceItemId']) if (n[k] !== undefined && !text(n[k])) return false;
  if (
    v.sourceDefinitionType !== undefined &&
    !['SUBSET', 'PARALLEL'].includes(String(v.sourceDefinitionType))
  )
    return false;
  if (
    v.parallel !== undefined &&
    (!object(v.parallel) ||
      !exactKeys(v.parallel, ['definitionId', 'name', 'sourceSubsetId', 'snapshotFingerprint']) ||
      !['definitionId', 'name', 'sourceSubsetId', 'snapshotFingerprint'].every((k) =>
        text((v.parallel as Record<string, unknown>)[k]),
      ))
  )
    return false;
  return true;
}
export const BATCH_TRANSFER_RESEARCH_FIELDS = [
  'listing',
  'draftDisagreements',
  'captureIssues',
  'catalogIssues',
  'imageOrientation',
] as const;
const priceAmount = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0 && v <= 100000000;
/** Only public http(s) links are displayed; credentials and alternate protocols are rejected. */
export function batchTransferSourceUrlIsValid(v: unknown): v is string {
  if (!text(v, 2000) || !/^https?:\/\//.test(v) || /\s/.test(v)) return false;
  try {
    const parsed = new URL(v);
    return ['https:', 'http:'].includes(parsed.protocol) && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
}
/** True when a side needs a new image: any quarter turn, or a deskew of at least the minimum angle. */
export function batchTransferOrientationSideIsValid(
  v: unknown,
): v is BatchTransferImageOrientationSide {
  if (!object(v) || !exactKeys(v, ['quarterTurns', 'deskewDegrees'])) return false;
  const turns = v.quarterTurns,
    deskew = v.deskewDegrees;
  if (turns !== undefined && ![0, 1, 2, 3].includes(turns as number)) return false;
  if (deskew !== undefined) {
    if (
      typeof deskew !== 'number' ||
      !Number.isFinite(deskew) ||
      Math.abs(deskew) < BATCH_TRANSFER_MIN_DESKEW_DEGREES ||
      Math.abs(deskew) > BATCH_TRANSFER_MAX_DESKEW_DEGREES ||
      Math.abs(deskew * 10 - Math.round(deskew * 10)) > 1e-9
    )
      return false;
  }
  return Boolean(turns) || deskew !== undefined;
}
function batchTransferOrientationIsValid(v: unknown): boolean {
  return (
    object(v) &&
    Object.keys(v).length > 0 &&
    exactKeys(v, ['front', 'back']) &&
    Object.values(v).every(batchTransferOrientationSideIsValid)
  );
}
function batchTransferResearchIsValid(row: Record<string, unknown>): boolean {
  if (row.imageOrientation !== undefined && !batchTransferOrientationIsValid(row.imageOrientation))
    return false;
  const listing = row.listing;
  if (listing !== undefined) {
    if (
      !object(listing) ||
      Object.keys(listing).length === 0 ||
      !exactKeys(listing, [
        'title',
        'description',
        'suggestedPrice',
        'priceBasis',
        'priceSources',
        'features',
        'presentation',
        'itemSpecifics',
      ]) ||
      (listing.title !== undefined && !text(listing.title, 77)) ||
      (listing.description !== undefined && !text(listing.description, 1000)) ||
      (listing.priceBasis !== undefined && !text(listing.priceBasis, 2000))
    )
      return false;
    if (listing.suggestedPrice !== undefined) {
      const price = listing.suggestedPrice;
      if (
        !object(price) ||
        !exactKeys(price, ['amountMinor', 'currency']) ||
        !priceAmount(price.amountMinor) ||
        price.currency !== 'USD' ||
        !text(listing.priceBasis, 2000)
      )
        return false;
    }
    if (
      listing.priceSources !== undefined &&
      (listing.suggestedPrice === undefined ||
        !Array.isArray(listing.priceSources) ||
        listing.priceSources.length === 0 ||
        listing.priceSources.length > 10 ||
        listing.priceSources.some(
          (source) =>
            !object(source) ||
            !exactKeys(source, ['url', 'kind', 'amountMinor', 'currency']) ||
            !batchTransferSourceUrlIsValid(source.url) ||
            !['SOLD', 'ACTIVE', 'OTHER'].includes(source.kind as string) ||
            (source.amountMinor !== undefined && !priceAmount(source.amountMinor)) ||
            (source.currency !== undefined && source.currency !== 'USD') ||
            (source.amountMinor !== undefined && source.currency !== 'USD'),
        ))
    )
      return false;
    if (
      listing.features !== undefined &&
      (!Array.isArray(listing.features) ||
        listing.features.length > EBAY_SPORT_CARD_FEATURES.length ||
        new Set(listing.features).size !== listing.features.length ||
        listing.features.some((feature) => !EBAY_SPORT_CARD_FEATURES.includes(feature as never)))
    )
      return false;
  }
  for (const [field, key, allowed, maximum] of [
    ['draftDisagreements', 'field', BATCH_TRANSFER_IDENTITY_FIELDS, 18],
    ['captureIssues', 'type', BATCH_TRANSFER_CAPTURE_ISSUE_TYPES, 10],
    ['catalogIssues', 'type', CHECKLIST_ISSUE_TYPES, 10],
  ] as const) {
    const notes = row[field];
    if (
      notes !== undefined &&
      (!Array.isArray(notes) ||
        notes.length === 0 ||
        notes.length > maximum ||
        notes.some(
          (note) =>
            !object(note) ||
            !exactKeys(note, [key, 'note']) ||
            !(allowed as readonly unknown[]).includes(note[key]) ||
            !text(note.note, 1000),
        ) ||
        (field === 'draftDisagreements' &&
          new Set(notes.map((note) => note.field)).size !== notes.length))
    )
      return false;
  }
  return true;
}
export function parseBatchIdentificationResults(
  value: unknown,
): { ok: true; value: BatchIdentificationResults } | { ok: false; error: string } {
  if (
    !object(value) ||
    !exactKeys(value, ['schemaVersion', 'exportId', 'batchId', 'runId', 'producer', 'cards']) ||
    ![1, 2].includes(value.schemaVersion as number)
  )
    return { ok: false, error: 'Unsupported result format or schema version.' };
  if (
    !text(value.exportId, 100) ||
    !/^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(value.exportId) ||
    !text(value.batchId) ||
    !/^tcvs:batch:[0-9A-HJKMNP-TV-Z]{26}$/.test(value.batchId) ||
    !text(value.runId, 100)
  )
    return { ok: false, error: 'Export, batch and run IDs are required.' };
  const producer = value.producer;
  if (
    !object(producer) ||
    !exactKeys(producer, ['tool', 'model', 'version']) ||
    !text(producer.tool, 100) ||
    ['model', 'version'].some((k) => producer[k] !== undefined && !text(producer[k], 200))
  )
    return { ok: false, error: 'Invalid producer information.' };
  if (
    !Array.isArray(value.cards) ||
    value.cards.length < 1 ||
    value.cards.length > BATCH_TRANSFER_MAX_CARDS
  )
    return { ok: false, error: 'The file must contain between 1 and 200 card results.' };
  const ids = new Set<string>();
  let normalizedCards: BatchIdentificationResult[] | undefined;
  for (const [index, row] of value.cards.entries()) {
    if (
      !object(row) ||
      !exactKeys(row, [
        'cardId',
        'inputFingerprint',
        'status',
        'identity',
        'confidence',
        'evidence',
        'candidates',
        'checklistSource',
        'checklistSourceSelection',
        ...(value.schemaVersion === 2 ? BATCH_TRANSFER_RESEARCH_FIELDS : []),
      ]) ||
      !cardId(row.cardId) ||
      typeof row.inputFingerprint !== 'string' ||
      !/^[a-f0-9]{64}$/.test(row.inputFingerprint) ||
      !BATCH_TRANSFER_RESULT_STATUSES.includes(row.status as never)
    )
      return { ok: false, error: 'Invalid card ID, image fingerprint or result status.' };
    if (object(row.listing) && Object.prototype.hasOwnProperty.call(row.listing, 'itemSpecifics')) {
      const specifics = parseBatchTransferItemSpecifics(
        row.listing.itemSpecifics,
        `cards[${index}].listing.itemSpecifics`,
      );
      if (!specifics.ok) return specifics;
    }
    if (object(row.listing) && Object.prototype.hasOwnProperty.call(row.listing, 'presentation')) {
      const presentation = parseBatchTransferPresentation(
        row.listing.presentation,
        `cards[${index}].listing.presentation`,
      );
      if (!presentation.ok) return presentation;
      normalizedCards ??= [...value.cards] as BatchIdentificationResult[];
      normalizedCards[index] = {
        ...row,
        listing: { ...row.listing, presentation: presentation.value },
      } as unknown as BatchIdentificationResult;
    }
    if (!batchTransferResearchIsValid(row))
      return {
        ok: false,
        error: 'Invalid listing suggestion, pricing evidence or research notes.',
      };
    if (ids.has(row.cardId as string))
      return { ok: false, error: 'A card ID appears more than once.' };
    ids.add(row.cardId as string);
    if (
      (row.status === 'MATCHED' && !batchTransferIdentityIsValid(row.identity)) ||
      (row.identity !== undefined && !batchTransferIdentityIsValid(row.identity)) ||
      !confidence(row.confidence) ||
      !evidence(row.evidence)
    )
      return { ok: false, error: 'Invalid identity, confidence, serial number or evidence.' };
    if (
      row.candidates !== undefined &&
      (!Array.isArray(row.candidates) ||
        row.candidates.length > 15 ||
        row.candidates.some(
          (c) =>
            !object(c) ||
            !exactKeys(c, ['identity', 'confidence', 'evidence']) ||
            !batchTransferIdentityIsValid(c.identity) ||
            !confidence(c.confidence) ||
            !evidence(c.evidence),
        ))
    )
      return { ok: false, error: 'Invalid candidate list.' };
    if (row.checklistSource !== undefined) {
      const s = row.checklistSource;
      if (
        !object(s) ||
        !exactKeys(s, [
          'sourceSystem',
          'sourceTargetId',
          'sourceItemId',
          'snapshotFingerprint',
          'parallelDefinitionId',
        ]) ||
        !['sourceSystem', 'sourceTargetId', 'sourceItemId'].every((k) => text(s[k])) ||
        ['snapshotFingerprint', 'parallelDefinitionId'].some(
          (k) => s[k] !== undefined && !text(s[k]),
        ) ||
        row.checklistSourceSelection !== undefined
      )
        return { ok: false, error: 'Invalid or conflicting checklist references.' };
    }
    if (
      row.checklistSourceSelection !== undefined &&
      !selectionIsValid(row.checklistSourceSelection)
    )
      return { ok: false, error: 'Invalid exact checklist selection.' };
  }
  return {
    ok: true,
    value: (normalizedCards === undefined
      ? value
      : { ...value, cards: normalizedCards }) as unknown as BatchIdentificationResults,
  };
}

/** Stable serialization for fingerprints; cryptographic hashing stays server-side. */
export function batchTransferStableJson(value: unknown): string {
  const canonical = (v: unknown): unknown =>
    Array.isArray(v)
      ? v.map(canonical)
      : object(v)
        ? Object.fromEntries(
            Object.keys(v)
              .sort()
              .filter((k) => v[k] !== undefined)
              .map((k) => [k, canonical(v[k])]),
          )
        : v;
  return JSON.stringify(canonical(value));
}
export function batchTransferReviewSnapshot(
  card: CardRecord | Record<string, unknown>,
): Record<string, unknown> {
  const fields = [
    'cardId',
    'placementState',
    'placementBatchId',
    'images',
    'imageRef',
    'retiredAt',
    'tombstonedAt',
    'ownerCardId',
    'placementGsiPk',
    's3Key',
    'pendingImageReplacement',
    'returnHold',
    'marketplaceSource',
    'identity',
    'importedIdentification',
    'identificationImportAudit',
    'identityStatus',
    'identityReviewDraft',
    'reviewChecklistSelection',
    'checklistSourceLineage',
    'manualIdentityOverride',
    'manualIdentityOverrideStatus',
    'checklistComparablePk',
    'commerceState',
    'grade',
    'gradeStatus',
    'gradeLabel',
    'cardPresentation',
    'listingPrice',
    'imageQualityStatus',
    'imageQualityReviewedAt',
  ];
  const raw = card as unknown as Record<string, unknown>;
  return Object.fromEntries(fields.filter((k) => raw[k] !== undefined).map((k) => [k, raw[k]]));
}
export function batchTransferImageFingerprintPayload(
  batchId: string,
  id: string,
  images: BatchTransferInternalCard['images'],
): unknown {
  return {
    batchId,
    cardId: id,
    images: Object.fromEntries(
      ['front', 'back']
        .filter((s) => images[s as 'front' | 'back'])
        .map((s) => {
          const image = images[s as 'front' | 'back']!;
          return [s, { key: image.key, versionId: image.versionId ?? null, etag: image.etag }];
        }),
    ),
  };
}
